import { useState, useEffect, RefObject, useMemo, forwardRef, useImperativeHandle } from "react";
import {
  RIF,
  timeConvert,
  TimeConvertType,
  useHandleEsc,
  sortBy,
  _,
  t,
  useCreateTaskColorTranslator,
} from "../../lib";
import {
  useSelectTheme,
  useSelectMethod,
  useSelectProjectAdherenceData,
  useSelectParticipantDataDigest,
  useSelectProjectDataDigest,
  useSelectParticipantGarminDirectDataDigest,
  useSelectParticipantGarminConnectDataDigest,
  ParticipantDataDigestMap,
  SelectedCellIndex,
  ParticipantDataDigest,
} from "../../store";
import {
  GarminConnectIcon,
  MessageIcon,
  AppleIcon,
  BellIcon,
  DeviceIcon,
  ChevronDownGreyIcon,
  CalendarIcon,
  LabfrontIcon,
  CheckSuccessIcon,
  WarningIcon,
  AndroidIcon,
  NewCloseIcon,
  HistoryIcon,
  LowBatteryIcon,
  BatteryIcon,
} from "../../asset/image";
import { Tooltip, Tag } from "..";
import {
  SidebarContent,
  AdherenceGarminConnect,
  AdherenceGarminTask,
  AdherenceNormalTask,
  AdherenceGarminDirect,
  getTaskContentName,
  DigestDay,
  TaskCompletionStatus,
} from "../../model";
import {
  TaskType,
  ITag,
  ITask,
  ITodo,
  ITimer,
  IQuestionnaire,
  IStopwatchGarminStream,
  IParticipant,
} from "../../shared/db";
import { TimeSlotWithTaskRecord } from "../../shared/mongo";
import { DateObject } from "react-multi-date-picker";
import moment from "moment";

export interface AdherenceSidebarProps {
  sidebarRef: RefObject<HTMLDivElement>;
  projectId?: string;
  taskList: ITask[];
  participantList: IParticipant[];
  garminDirectEnabled: boolean;
  garminConnectEnabled: boolean;
  garminDeviceTask?: ITask;
  projectHasScheduleTask: boolean;
  hasDexcom: boolean;
  sendMessage: (participantList: IParticipant[]) => void;
}

export type SidebarControlRefType = {
  setDisplay: (display: boolean) => void;
  setSelectedCellIndex: (cellIndex: SelectedCellIndex) => void;
};

enum ScheduleTimeSlotCompleteState {
  Completed,
  NotCompleteYet,
  Missed,
}

interface TimeSlotWithTaskRecordForAdherece extends TimeSlotWithTaskRecord {
  completeState: ScheduleTimeSlotCompleteState;
  yymmddStart?: number;
  yymmddEnd?: number;
  yymmddComplete?: number;
}

interface RecentActivityContent {
  icon: string;
  title: string;
  toolTipContent: string;
  timestamp?: number;
  jsxContent?: JSX.Element;
}

const lastSeenActivityContent: RecentActivityContent = {
  icon: LabfrontIcon,
  title: "Last seen",
  toolTipContent:
    "This denotes the most recent time that the participant’s Labfront Companion app was connected to the internet.",
};

const garminDeviceLastSyncActivityContent: RecentActivityContent = {
  icon: LabfrontIcon,
  title: "Last sync complete",
  toolTipContent: "This denotes the most recent time that data was successfully uploaded.",
};

const garminConnectLastSyncActivityContent: RecentActivityContent = {
  icon: GarminConnectIcon,
  title: "Last upload complete",
  toolTipContent:
    "This denotes the timestamp of the last data point received from Garmin Connect. \n NOTE: Under normal conditions, each time GC opens, it sends us data, so it is a decent approximation.",
};

export const AdherenceSidebar = forwardRef((props: AdherenceSidebarProps, ref) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const colorTranslator = useCreateTaskColorTranslator();

  const {
    sidebarRef,
    projectId,
    taskList,
    participantList,
    garminDirectEnabled,
    garminConnectEnabled,
    garminDeviceTask,
    projectHasScheduleTask,
    hasDexcom,
    sendMessage,
    // setHighLightBox,
  } = props;

  const [display, setDisplay] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<SelectedCellIndex | undefined>();
  const [sidebarContent, setSidebarContent] = useState<SidebarContent | null>(null);

  useImperativeHandle(ref, () => ({
    setDisplay: (display: boolean) => {
      setDisplay(display);
    },

    setSelectedCellIndex: (cellIndex: SelectedCellIndex) => {
      setSelectedCell(cellIndex);
    },
  }));

  const currentParticipantAdherence =
    useSelectProjectAdherenceData(projectId)?.participantAdherence;
  const participantDataDigestMap: ParticipantDataDigestMap =
    useSelectProjectDataDigest()?.[projectId ?? ""]?.participantDataDigestMap || {};

  const currentSidebarContentIndex = useMemo<SelectedCellIndex>(() => {
    if (selectedCell) {
      return {
        participantId: selectedCell.participantId,
        yymmddIndex: selectedCell.yymmddIndex,
      };
    }
    return {
      participantId: "NA",
      yymmddIndex: 0,
    };
  }, [selectedCell]);

  const garminDirectDataDigest = useSelectParticipantGarminDirectDataDigest(
    projectId,
    currentSidebarContentIndex.participantId,
    currentSidebarContentIndex.yymmddIndex.toString(),
  );

  const garminConnectDataDigest = useSelectParticipantGarminConnectDataDigest(
    projectId,
    currentSidebarContentIndex.participantId,
    currentSidebarContentIndex.yymmddIndex.toString(),
  );

  const sidebarDigestDayDetail = useSelectParticipantDataDigest(
    currentSidebarContentIndex.participantId,
  )?.[currentSidebarContentIndex.yymmddIndex];

  const sidebarContentDate = useMemo(() => {
    return sidebarContent?.yymmddIndex ? t.formattedDate(sidebarContent.yymmddIndex) : "";
  }, [sidebarContent]);

  const MINUTES_IN_ONE_HOUR = 60;
  const garminConnectWearTime = useMemo(() => {
    if (!garminConnectEnabled) return 0;
    return garminConnectDataDigest?.wearTime ?? 0;
  }, [garminConnectDataDigest]);

  const MILLISECONDS_IN_ONE_HOUR = 3600000;
  const garminDirectWearTime = useMemo(() => {
    if (!garminDirectEnabled) return -1;
    return garminDirectDataDigest?.wearTime ?? 0;
  }, [currentSidebarContentIndex]);

  useEffect(() => {
    // if (!displaySidebar) return
    // if (sidebarContent?.yymmddIndex) {
    //   setDisplayDate(
    //     timeConvert({time: sidebarContent?.yymmddIndex, type: TimeConvertType.adherenceSidebarTitle}) as string,
    //   )
    // }

    // if (garminDirectEnabled) {
    //   let garminTaskGarminData = null
    //   if (sidebarContent?.task && Object.keys(sidebarContent?.task).length) {
    //     garminTaskGarminData = _.find(sidebarContent?.task, ['taskType', TaskType.GarminDevice]) as AdherenceGarminTask
    //   }

    // if (garminTaskGarminData) {
    //   setTaskGarminData(garminTaskGarminData)
    // } else {
    //   setTaskGarminData({
    //     taskId: '',
    //     taskType: 'garmin_device',
    //   })
    // }
    // }

    // scroll to top when click diff field
    if (!sidebarRef?.current) return;
    sidebarRef.current.scrollTop = 0;
  }, [display]);

  useEffect(() => {
    if (!selectedCell) return;

    const participantId = selectedCell?.participantId;
    const yymmddIndex = selectedCell?.yymmddIndex;
    const participantAdherence = currentParticipantAdherence?.[participantId];
    const participant = participantList.find((p) => p.id === participantId);
    if (!participantAdherence) return;
    const participantName = participantAdherence?.status?.participantInsignia ?? "Unknown";
    const digestDay = participantAdherence?.digestDayMap?.[yymmddIndex];
    const participantActivatedYymmdd = t.getYymmddFromIsoAndTimezone(
      participant?.activatedDate,
      participant?.activatedTimezone,
    );
    const dayCount = getActivatedRelativeDayCount(
      +new DateObject().format("YYMMDD"),
      participantActivatedYymmdd,
    );
    const sidebarContentDayCount = getActivatedRelativeDayCount(
      yymmddIndex,
      participantActivatedYymmdd,
    );

    if (!digestDay) {
      setSidebarContent({
        yymmddIndex: yymmddIndex,
        participantId: participantId,
        participantName: participant?.insignia ?? "",
        status: participantAdherence?.status,
        taskCompletionStatus: [],
        tagList: participant?.tagList ?? [],
        dayCount,
        sidebarContentDayCount,
        notification: participant?.notificationEnabled ?? false,
      });
    } else {
      const taskCompletionStatus: TaskCompletionStatus[] = taskList
        .map((task, index) => {
          const result = {
            name: task[getTaskContentName(task.type)]?.name ?? "Unknown Task",
            index: (task.index ?? index) + 1,
            color: task.color ? `#${task.color}` : colorTranslator(task.index ?? 0),
          };
          const scheduleTaskState = digestDay.scheduleTaskStateMap?.[task.id];
          if (scheduleTaskState) {
            return {
              display: true,
              ...result,
              completionCount: scheduleTaskState?.completedCount ?? 0,
              totalCount: scheduleTaskState?.totalCount ?? 0,
            };
          } else {
            const completedCount = digestDay?.taskCompletionCountMap?.[task.id] ?? 0;
            return {
              display: completedCount > 0,
              ...result,
              completionCount: completedCount,
              totalCount: 0,
            };
          }
        })
        .filter((taskStatus) => taskStatus.display)
        .sort((a, b) => {
          if (a && b) {
            return a.index - b.index;
          } else {
            return 0;
          }
        });

      const participantDataDigest = participantDataDigestMap[participantId];
      setSidebarContent({
        digestDay,
        yymmddIndex: digestDay.yymmddIndex ?? yymmddIndex,
        garminConnectEnabled: garminConnectEnabled,
        participantId: participantId,
        participantName: participantName,
        participantActivatedYymmdd: participantActivatedYymmdd,
        status: participantAdherence?.status,
        taskCompletionStatus: taskCompletionStatus,
        tagList: participant?.tagList ?? [],
        dayCount,
        sidebarContentDayCount,
        notification: participant?.notificationEnabled ?? false,
        scheduleTaskRecordMap: fetchScheduTaskRecordMapFromParticipantDataDigest(
          participantDataDigest,
          digestDay,
        ),
      });
    }
  }, [selectedCell]);

  const fetchScheduTaskRecordMapFromParticipantDataDigest = (
    participantDataDigest: ParticipantDataDigest,
    digestDay: DigestDay,
  ) => {
    if (digestDay.scheduleTaskStateMap) {
      const result: { [taskId: string]: TimeSlotWithTaskRecordForAdherece[] } = {};

      const now = new Date();
      const shiftedNow = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000);

      for (const [taskId, scheduleTaskState] of Object.entries(digestDay.scheduleTaskStateMap)) {
        if (scheduleTaskState.timeSlotList) {
          result[taskId] = scheduleTaskState.timeSlotList.map((timeSlot) => {
            let completeState = timeSlot.taskRecord
              ? ScheduleTimeSlotCompleteState.Completed
              : ScheduleTimeSlotCompleteState.Missed;

            if (completeState === ScheduleTimeSlotCompleteState.Missed) {
              if (timeSlot?.end && new Date(timeSlot?.end) > shiftedNow) {
                completeState = ScheduleTimeSlotCompleteState.NotCompleteYet;
              }
            }

            return {
              ...timeSlot,
              completeState,
            };
          });
        }
        if (scheduleTaskState.multiDayTimeSlotIdList) {
          const multiDayTimeSlotRecordMap = participantDataDigest.taskMultiDayTimeSlotMap;
          if (multiDayTimeSlotRecordMap) {
            for (const timeSlotId of scheduleTaskState.multiDayTimeSlotIdList) {
              const timeSlotReocrd = multiDayTimeSlotRecordMap[taskId]?.get(timeSlotId);
              if (timeSlotReocrd) {
                result[taskId] ??= [];
                let completeState = timeSlotReocrd.yymmddComplete
                  ? ScheduleTimeSlotCompleteState.Completed
                  : ScheduleTimeSlotCompleteState.Missed;

                const notCompleteYet = timeSlotReocrd.yymmddComplete
                  ? timeSlotReocrd.yymmddComplete != digestDay.yymmddIndex
                  : timeSlotReocrd.yymmddEnd != digestDay.yymmddIndex;

                if (notCompleteYet) {
                  completeState = ScheduleTimeSlotCompleteState.NotCompleteYet;
                }

                result[taskId].push({
                  ...timeSlotReocrd,
                  completeState,
                });
              }
            }
          }
        }

        if (result[taskId]) {
          result[taskId] = result[taskId].sort((a, b) => (a.start < b.start ? -1 : 1));
        }
      }
      return result;
    }
  };

  const getActivatedRelativeDayCount = (comparisonYymmdd: number, activatedYymmdd?: number) => {
    if (!activatedYymmdd) return "Not Activated";

    const diffDays = t.getYymmddDiffInDays(activatedYymmdd, comparisonYymmdd);
    return diffDays >= 0 ? `Day ${diffDays + 1}` : "Not Activated";
  };

  const handleClose = () => {
    setDisplay(false);
  };

  const handleSendMessage = () => {
    const participant = participantList.find((p) => p.id === selectedCell?.participantId);
    if (participant) sendMessage([participant]);
  };
  // press esc to cancel
  useHandleEsc(handleClose);

  const [expandRecentActivity, setExpandRenderActivity] = useState(false);

  const formatDate = (timeZone: string): string => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(new Date());
    const partMap: { [key: string]: string } = {};
    parts.forEach((part) => {
      if (part.type !== "literal") partMap[part.type] = part.value;
    });

    return `${partMap.month} ${partMap.day}, ${partMap.hour}:${partMap.minute}`;
  };

  const formatTime = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} hrs ${minutes} min`;
  };

  // const [garminConnectCollectionCount, setGarminConnectCollectionCount] = useState(0)
  const garminConnectCollectionCount = useMemo(() => {
    if (!sidebarDigestDayDetail?.garminConnect) return 0;
    return (
      (sidebarDigestDayDetail?.garminConnect.healthDailiesCollected ? 3 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthEpochsCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthSleepsCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthStressCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthRespirationCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthBodyCompositionsCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthPulseOxCollected ? 1 : 0) +
      (sidebarDigestDayDetail?.garminConnect.healthSkinTemperatureCollected ? 1 : 0)
    );
  }, [sidebarDigestDayDetail?.garminConnect]);

  const garminDeviceBatteryLevelContent = (
    batteryLevel?: number,
    timestamp?: number,
  ): RecentActivityContent => {
    if (!batteryLevel || !timestamp) {
      return {
        icon: BatteryIcon,
        title: "Garmin device battery level",
        toolTipContent: `Battery level updates when the device syncs.`,
      };
    }

    const jsxContent =
      batteryLevel < 20 ? (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <img src={LowBatteryIcon} />
          <span
            css={{
              color: color.warning,
              fontSize: fontSize.h7,
            }}
          >
            {batteryLevel > 0 ? `${batteryLevel}%` : "Unknown"}
          </span>
        </div>
      ) : (
        <>
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
            }}
          >
            {batteryLevel}%
          </span>
        </>
      );

    return {
      icon: BatteryIcon,
      title: "Garmin device battery level",
      jsxContent,
      toolTipContent: `Battery level updates when the device syncs.
      
      Updated at ${t.formatTimestampToPrettyDate(timestamp)}`,
    };
  };

  const recentActivityContents: RecentActivityContent[] = useMemo(() => {
    lastSeenActivityContent.timestamp = sidebarContent?.status.unixTimestampLastAppOpen;
    const contents: RecentActivityContent[] = [lastSeenActivityContent];

    if (garminDirectEnabled) {
      garminDeviceLastSyncActivityContent.timestamp =
        sidebarContent?.status.unixTimestampLastGarminDeviceSync;
      contents.push(garminDeviceLastSyncActivityContent);

      const { deviceBatteryPercentage, deviceBatteryPercentageTimestamp } =
        sidebarContent?.status ?? {};
      contents.push(
        garminDeviceBatteryLevelContent(deviceBatteryPercentage, deviceBatteryPercentageTimestamp),
      );
    }

    if (garminConnectEnabled) {
      garminConnectLastSyncActivityContent.timestamp =
        sidebarContent?.status.unixTimestampLastGarminConnectSync;
      contents.push(garminConnectLastSyncActivityContent);
    }
    return contents;
  }, [sidebarContent]);

  const recentActivityItem = (recentActivityContent: RecentActivityContent) => {
    const { icon, title, toolTipContent, timestamp, jsxContent } = recentActivityContent;

    const timeString = timeConvert({
      time: timestamp,
      type: TimeConvertType.lastSeen,
    }) as string;

    return (
      <div
        key={title}
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
        }}
      >
        <div
          css={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img src={icon} width={16} alt="Recent activity icon" />
            <span
              css={{
                color: color.textIcon.secondary,
                fontSize: fontSize.h7,
              }}
            >
              {title}
            </span>
            <Tooltip content={toolTipContent} />
          </div>
          {jsxContent}
          {(timestamp || timestamp === 0) && (
            <span
              css={{
                color: color.textIcon.secondary,
                fontSize: fontSize.h7,
              }}
            >
              {timeString}
            </span>
          )}
        </div>
      </div>
    );
  };

  const participantCurrentDateTime = () => {
    return (
      <>
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <span css={{ color: color.textIcon.secondary, fontSize: fontSize.h7 }}>
            Participant's current time:&nbsp;
          </span>
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
              fontWeight: fontWeight.bold,
            }}
          >
            {formatDate(sidebarContent?.status.timezone ?? "UTC")} {`(${sidebarContent?.dayCount})`}
          </span>
        </div>
      </>
    );
  };

  const garminConnectDataCollectedStatus = () => {
    return (
      <div
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={GarminConnectIcon} width={16} alt="Garmin Connect icon" />
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
            }}
          >
            Garmin Connect App
          </span>
        </div>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* garmin connect progress bar */}
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {new Array(9).fill(1).map((c, i) => (
              <div
                key={i}
                css={{
                  width: 4,
                  height: 8,
                  borderRadius: 1,
                  backgroundColor:
                    i < garminConnectCollectionCount
                      ? color.textIcon.title
                      : color.surface.smallComponent,
                }}
              />
            ))}
          </div>
          {/* garmin connect text */}
          <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
            {garminConnectCollectionCount}&nbsp;/ 9 Collected
          </span>
        </div>
      </div>
    );
  };

  const garminDirectDataCollectedStatus = () => {
    return (
      <div
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={LabfrontIcon} width={16} alt="Labfront Companion icon" />
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
            }}
          >
            Labfront Companion App
          </span>
        </div>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* labfront companion progress bar */}
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {Object.entries(garminDeviceTask?.garminDevice ?? {})
              .filter(([key, value]) => key.endsWith("Enable") && value)
              .map((c, i) => (
                <div
                  key={c[0]}
                  css={{
                    width: 4,
                    height: 8,
                    borderRadius: 1,
                    backgroundColor:
                      i <
                      Object.values(sidebarContent?.digestDay?.garminDirectAdherence ?? {}).filter(
                        (i) => i,
                      ).length
                        ? color.textIcon.link
                        : color.surface.smallComponent,
                  }}
                />
              ))}
          </div>
          {/* labfront companion text */}
          <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
            {
              Object.values(sidebarContent?.digestDay?.garminDirectAdherence ?? {}).filter((i) => i)
                .length
            }{" "}
            /{" "}
            {
              Object.entries(garminDeviceTask?.garminDevice ?? {}).filter(
                ([key, value]) => key.endsWith("Enable") && value,
              ).length
            }{" "}
            Collected
          </span>
        </div>
      </div>
    );
  };

  const ProgressBar = (props: { progress: number; backgroundColor: string }) => {
    const { progress, backgroundColor } = props;
    return (
      <div
        css={{
          width: 24,
          height: 4,
          borderRadius: 999,
          backgroundColor: color.surface.smallComponent,
          overflow: "hidden",
          display: "flex",
        }}
      >
        <div
          css={{
            width: `${progress * 100}%`,
            height: "100%",
            backgroundColor: backgroundColor,
          }}
        />
      </div>
    );
  };

  const garminConnectWearTimeProgress = () => {
    return (
      <div
        css={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={GarminConnectIcon} width={16} alt="Garmin Connect icon" />
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
            }}
          >
            Garmin Connect App
          </span>
        </div>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* total wear time progress bar */}
          <ProgressBar
            {...{
              progress: Math.floor(garminConnectWearTime / MINUTES_IN_ONE_HOUR) / 24,
              backgroundColor: color.black,
            }}
          ></ProgressBar>

          {/* total wear time text */}
          <span
            css={{
              color: color.textIcon.title,
              fontSize: fontSize.h7,
            }}
          >
            {formatTime(garminConnectWearTime)}
          </span>
        </div>
      </div>
    );
  };

  const garminDirectWearTimeProgress = () => {
    return (
      <div
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={LabfrontIcon} width={16} alt="Labfront Companion icon" />
          <span
            css={{
              color: color.textIcon.secondary,
              fontSize: fontSize.h7,
            }}
          >
            Labfront Companion App
          </span>
        </div>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* total wear time progress bar */}
          <ProgressBar
            {...{
              progress: Math.floor(garminDirectWearTime / MILLISECONDS_IN_ONE_HOUR) / 24,
              backgroundColor: color.surface.blue.brand,
            }}
          ></ProgressBar>

          {/* total wear time text */}
          <span
            css={{
              color: color.textIcon.title,
              fontSize: fontSize.h7,
            }}
          >
            {formatTime(Math.floor(garminDirectWearTime / 60000))}
          </span>
        </div>
      </div>
    );
  };

  const shortenString = (str: string, maxLength: number) => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength - 3) + "...";
  };

  return (
    <div
      ref={sidebarRef}
      css={{
        position: "fixed",
        right: 0,
        top: "0",
        zIndex: "104",
        // width: '30%',
        height: "100vh",
        overflowY: "scroll",
        width: display ? "400px" : "0",
        transition: ".3s ease-in-out",
        background: color.white,
        boxShadow: "-8px 0px 36px 0px rgba(0, 0, 0, 0.16)",
      }}
    >
      {/* top section */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          padding: 16,
          width: "100%",
          borderBottom: `1px solid ${color.border.light}`,
          backgroundColor: "#EFF0F4",
          gap: 16,
        }}
      >
        {/* insignia and msg button, close button */}
        <div
          css={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            css={{
              fontSize: fontSize.h4,
              fontWeight: fontWeight.thick,
            }}
          >
            {sidebarContent?.participantName}
          </span>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <button
              type="button"
              aria-label="Send message"
              onClick={handleSendMessage}
              css={{
                width: 32,
                height: 32,
                border: "none",
                borderRadius: 4,
                backgroundColor: "transparent",
                backgroundImage: `url(${MessageIcon})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: 24,
                backgroundPosition: "center",
                cursor: "pointer",
                ":hover": {
                  backgroundColor: color.surface.hover.hoverGreyBg,
                },
              }}
            />
            <button
              onClick={handleClose}
              css={{
                width: 32,
                height: 32,
                border: "none",
                borderRadius: 4,
                backgroundColor: "transparent",
                backgroundImage: `url(${NewCloseIcon})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: 24,
                backgroundPosition: "center",
                cursor: "pointer",
                ":hover": {
                  backgroundColor: color.surface.hover.hoverGreyBg,
                },
              }}
            />
          </div>
        </div>
        {/* tag & participant's current time */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            width: "100%",
          }}
        >
          {RIF(
            sidebarContent?.tagList?.length,
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {sidebarContent?.tagList?.map((tag: ITag) => (
                <Tag
                  key={tag.id}
                  {...{
                    tag,
                  }}
                />
              ))}
            </div>,
          )}
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          ></div>
        </div>
        {/* adherence status */}
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 16,
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img
              src={sidebarContent?.status.mobileType === "ios" ? AppleIcon : AndroidIcon}
              width={16}
              alt={sidebarContent?.status.mobileType === "ios" ? "Apple iOS icon" : "Android icon"}
            />
            <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
              {sidebarContent?.status.mobileType
                ? sidebarContent?.status.mobileType === "ios"
                  ? "IOS"
                  : "Android"
                : "N/A"}
            </span>
            <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
              {sidebarContent?.status.mobileOsVersion}
            </span>
          </div>
          <div css={{ width: 1, height: "100%", backgroundColor: color.border.default }} />
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img src={LabfrontIcon} width={16} alt="Labfront Companion icon" />
            <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
              {sidebarContent?.status?.appVersion
                ? `v ${sidebarContent?.status?.appVersion?.split("(")[0]}`
                : "N/A"}
            </span>
          </div>
          <div css={{ width: 1, height: "100%", backgroundColor: color.border.default }} />
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img src={BellIcon} width={16} alt="Notification status icon" />
            <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
              {sidebarContent?.notification ? "ON" : "OFF"}
            </span>
          </div>
          <div css={{ width: 1, height: "100%", backgroundColor: color.border.default }} />
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img src={DeviceIcon} width={16} alt="Device info icon" />
            <span css={{ color: color.textIcon.title, fontSize: fontSize.h7 }}>
              {sidebarContent?.status.deviceType ?? "No device"}
              {sidebarContent?.status.deviceFirmware
                ? `, v${sidebarContent?.status.deviceFirmware}`
                : ""}
            </span>
          </div>
        </div>
        {/* recent activity */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            padding: "8px 16px",
            borderRadius: 8,
            border: `1px solid ${color.border.light}`,
            backgroundColor: color.surface.default,
            height: expandRecentActivity ? 40 + recentActivityContents.length * 33 : 40,
            transition: ".5s ease-in-out",
            overflow: "hidden",
          }}
        >
          <button
            onClick={() => setExpandRenderActivity(!expandRecentActivity)}
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <span
              css={{
                color: color.textIcon.title,
                fontSize: fontSize.h7,
                fontWeight: fontWeight.thick,
              }}
            >
              Recent Activity
            </span>
            <img
              css={{
                transform: expandRecentActivity ? "rotate(180deg)" : "rotate(0)",
                transition: ".5s ease-in-out",
              }}
              src={ChevronDownGreyIcon}
              width={24}
              height={24}
              alt={expandRecentActivity ? "Expanded status icon" : "Collapsed status icon"}
            />
          </button>
          {/* recent activity content */}
          {recentActivityContents.map((content) => recentActivityItem(content))}
        </div>
      </div>

      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 24,
          width: "100%",
          padding: 16,
        }}
      >
        {/* current calendar date & participant relative day */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            css={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <img src={CalendarIcon} width={20} alt="Calendar icon" />
              <span
                css={{
                  fontSize: fontSize.h4,
                  fontWeight: fontWeight.bold,
                  color: color.textIcon.title,
                }}
              >
                {sidebarContentDate}
              </span>
            </div>
            <span
              css={{
                fontSize: fontSize.h4,
                fontWeight: fontWeight.bold,
                color: color.textIcon.title,
              }}
            >
              {sidebarContent?.sidebarContentDayCount}
            </span>
          </div>
          {participantCurrentDateTime()}
        </div>

        {/* digest day */}
        <div
          css={{
            width: "100%",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 24,
            borderRadius: 8,
            border: `1px solid ${color.border.blueLight}`,
            backgroundColor: color.surface.blue.light,
          }}
        >
          <div
            css={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {RIF(garminConnectEnabled, garminConnectWearTimeProgress())}
            {RIF(garminDirectEnabled, garminDirectWearTimeProgress())}

            {/* {RIF(garminConnectEnabled, garminConnectDataCollectedStatus())} */}
            {/* {RIF(garminDirectEnabled, garminDirectDataCollectedStatus)} */}

            {/* tasks */}
            {sidebarContent?.taskCompletionStatus.map((c) => (
              <div
                key={c.name}
                css={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    css={{
                      width: 16,
                      minWidth: 16,
                      height: 16,
                      minHeight: 16,
                      borderRadius: "50%",
                      backgroundColor: c.color,
                      color: color.textIcon.white,
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.thick,
                      textAlign: "center",
                    }}
                  >
                    {c.index}
                  </span>
                  <span
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    {shortenString(c.name, 40)}
                  </span>
                </div>
                <span
                  css={{
                    color: color.textIcon.title,
                    fontSize: fontSize.h7,
                  }}
                >
                  {RIF(c.totalCount > 0, `${c.completionCount} / ${c.totalCount} Completed`)}
                  {RIF(c.totalCount == 0, `${c.completionCount} times`)}
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* data collection & task completion */}
        <DataCollectionAndTaskCompletion
          {...{
            garminDirectEnabled,
            garminConnectEnabled,
            garminDeviceTask,
            garminConnectCollectionStatus: sidebarDigestDayDetail?.garminConnect,
            garminDirectAdherence: sidebarContent?.digestDay?.garminDirectAdherence,
            garminDirectCollected: sidebarContent?.digestDay?.garminDirectCollected,
            regularTaskCompletion: sidebarDigestDayDetail?.task,
            scheduleTaskRecordMap: sidebarContent?.scheduleTaskRecordMap,
            participantActivatedYymmdd: sidebarContent?.participantActivatedYymmdd,
          }}
        />
      </div>
    </div>
  );
});

const DataCollectionAndTaskCompletion = (props: {
  garminDirectEnabled: boolean;
  garminConnectEnabled: boolean;
  garminConnectCollectionStatus?: AdherenceGarminConnect;
  garminDeviceTask?: ITask;
  garminDirectAdherence?: AdherenceGarminDirect;
  regularTaskCompletion?: { [key: string]: AdherenceNormalTask | AdherenceGarminTask };
  scheduleTaskRecordMap?: { [taskId: string]: TimeSlotWithTaskRecord[] };
  participantActivatedYymmdd?: number;
}) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const {
    garminDirectEnabled,
    garminConnectEnabled,
    garminConnectCollectionStatus,
    garminDeviceTask,
    garminDirectAdherence,
    regularTaskCompletion,
    scheduleTaskRecordMap,
    participantActivatedYymmdd,
  } = props;
  const method = useSelectMethod();
  const taskList =
    method?.taskList
      ?.filter((item: ITask) => item.enabled && item.type !== TaskType.GarminDevice)
      .sort(sortBy("index")) ?? [];
  const garminEnabled = garminDirectEnabled || garminConnectEnabled;

  enum Tab {
    DataCollection = "dataCollection",
    TaskCompletion = "taskCompletion",
  }

  const [tab, setTab] = useState<Tab>(Tab.TaskCompletion);
  const toReadable = (str: string) => {
    const withoutEnable = str.replace(/Enable$/i, "");
    const withSpaces = withoutEnable.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase()).trim();
  };

  // Filter out regular tasks that are also present in scheduleTaskRecordMap
  const filteredRegularTaskCompletion = useMemo(() => {
    if (!scheduleTaskRecordMap) return regularTaskCompletion;
    if (!regularTaskCompletion) return undefined;
    return Object.fromEntries(
      Object.entries(regularTaskCompletion).filter(([taskId]) => !scheduleTaskRecordMap[taskId]),
    );
  }, [regularTaskCompletion, scheduleTaskRecordMap]);

  useEffect(() => {
    setTab(garminEnabled ? Tab.DataCollection : Tab.TaskCompletion);
  }, [garminEnabled]);

  const calculateAvailableTime = (start: Date, end?: Date) => {
    if (participantActivatedYymmdd) {
      const startDate = new Date(start);
      const startYymmdd = +t.formatUTCDateToYYMMDDIndex(startDate);
      const startDayNum = t.getYymmddDiffInDays(participantActivatedYymmdd, startYymmdd) + 1;
      const startTimeStr = t.formatUtcTime(startDate);

      if (end) {
        const endDate = new Date(end);
        const endYymmdd = +t.formatUTCDateToYYMMDDIndex(endDate);
        const endDayNum = t.getYymmddDiffInDays(participantActivatedYymmdd, endYymmdd) + 1;
        const endTimeStr = t.formatUtcTime(endDate);
        return startDayNum === endDayNum
          ? `Day ${startDayNum}, ${startTimeStr} - ${endTimeStr}`
          : `Day ${startDayNum}, ${startTimeStr} - Day ${endDayNum}, ${endTimeStr}`;
      } else {
        return `Day ${startDayNum}, ${startTimeStr} - project end`;
      }
    }
  };

  const parseCompleteDate = (completeTimestamp: number, timezone: string) => {
    if (participantActivatedYymmdd) {
      const date = new Date(completeTimestamp);
      const completeYymmdd = t.getYymmddFromIsoAndTimezone(date, timezone);
      if (completeYymmdd) {
        const dayNum = t.getYymmddDiffInDays(participantActivatedYymmdd, completeYymmdd) + 1;
        const timeStr = moment.tz(new Date(completeTimestamp), timezone).format("HH:mm");
        return `Day ${dayNum}, ${timeStr}`;
      } else {
        return "N/A";
      }
    }
    return `${new Date(completeTimestamp)}`;
  };

  const RegularTaskCompletion = (
    regularTaskCompletion: { [key: string]: AdherenceNormalTask } | undefined,
  ) => {
    return (
      <>
        {taskList.map((task, index) => {
          const record = regularTaskCompletion?.[task.id];
          if (!record) return null;

          return (
            <div
              key={task.id}
              css={{
                width: "368px",
                minWidth: "368px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                css={{
                  display: "flex",
                  gap: 8,
                }}
              >
                <TaskDot
                  {...{
                    backgroundColor: task.color ? `#${task.color}` : color.task.yellow,
                    text: `${(task.index ?? index) + 1}`,
                  }}
                />
                <span
                  css={{
                    fontWeight: fontWeight.bold,
                  }}
                >
                  {(
                    task[getTaskContentName(task.type) as keyof ITask] as
                      | ITodo
                      | ITimer
                      | IQuestionnaire
                      | IStopwatchGarminStream
                  ).name ?? ""}
                </span>
              </div>
              <div
                css={{
                  width: "100%",
                  borderRadius: 8,
                  border: `1px solid ${color.border.light}`,
                }}
              >
                <div
                  css={{
                    width: "100%",
                    padding: "8px 16px",
                    backgroundColor: color.surface.default,
                    display: "flex",
                    gap: 16,
                  }}
                >
                  <span
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Task Completion Status
                  </span>
                </div>
                {RIF(
                  record.timestampList,
                  record.timestampList.map((r) => (
                    <div
                      key={r.completionId}
                      css={{
                        width: "100%",
                        height: "48px",
                        maxHeight: "48px",
                        padding: "8px 16px",
                        display: "flex",
                        gap: 16,
                      }}
                    >
                      {r.unixTimestampComplete && (
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <img src={CheckSuccessIcon} width={14} alt="Completed icon" />
                          <span
                            css={{
                              color: color.textIcon.secondary,
                              fontSize: fontSize.h7,
                            }}
                          >
                            Completed at&nbsp;
                            <span
                              css={{
                                color: color.textIcon.title,
                                fontSize: fontSize.h7,
                                fontWeight: fontWeight.bold,
                              }}
                            >
                              {parseCompleteDate(r.unixTimestampComplete, r.timezone)}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  )),
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const ScheduleTaskCompletion = (
    scheduleTaskRecordMap: { [taskId: string]: TimeSlotWithTaskRecordForAdherece[] } | undefined,
  ) => {
    return (
      <>
        {taskList.map((task, index) => {
          const record = scheduleTaskRecordMap?.[task.id];
          if (!record) return null;

          return (
            <div
              key={task.id}
              css={{
                width: "368px",
                minWidth: "368px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                css={{
                  display: "flex",
                  gap: 8,
                }}
              >
                <TaskDot
                  {...{
                    backgroundColor: task.color ? `#${task.color}` : color.task.yellow,
                    text: `${(task.index ?? index) + 1}`,
                  }}
                />
                <span
                  css={{
                    fontWeight: fontWeight.bold,
                  }}
                >
                  {(
                    task[getTaskContentName(task.type) as keyof ITask] as
                      | ITodo
                      | ITimer
                      | IQuestionnaire
                      | IStopwatchGarminStream
                  ).name ?? ""}
                </span>
              </div>
              <div
                css={{
                  width: "100%",
                  borderRadius: 8,
                  border: `1px solid ${color.border.light}`,
                  overflow: "hidden",
                }}
              >
                <div
                  css={{
                    width: "100%",
                    padding: "8px 16px",
                    backgroundColor: color.surface.default,
                    display: "flex",
                    gap: 16,
                  }}
                >
                  <span
                    css={{
                      width: 96,
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Available Time
                  </span>
                  <span
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Task Completion Status
                  </span>
                </div>
                {record.map((r) => ScheduleTaskCompletionRow(r))}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const ScheduleTaskCompletionRow = (data: TimeSlotWithTaskRecordForAdherece) => {
    return (
      <div
        key={data.id}
        css={{
          width: "100%",
          height: "48px",
          maxHeight: "48px",
          padding: "8px 16px",
          display: "flex",
          gap: 16,
        }}
      >
        <span
          css={{
            width: 96,
            color: color.textIcon.secondary,
            fontSize: fontSize.h7,
          }}
        >
          {calculateAvailableTime(data.start, data.end)}
        </span>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {data.taskRecord && data.completeState == ScheduleTimeSlotCompleteState.Completed && (
            <>
              <img src={CheckSuccessIcon} width={14} alt="Completed icon" />
              <span
                css={{
                  color: color.textIcon.secondary,
                  fontSize: fontSize.h7,
                }}
              >
                Completed at&nbsp;
                <span
                  css={{
                    color: color.textIcon.title,
                    fontSize: fontSize.h7,
                  }}
                >
                  {parseCompleteDate(
                    data.taskRecord.data.unixTimestampComplete,
                    data.taskRecord.timezone,
                  )}
                </span>
              </span>
            </>
          )}

          {data.completeState == ScheduleTimeSlotCompleteState.NotCompleteYet && (
            <>
              <img src={HistoryIcon} width={14} alt="Not completed icon" />
              <span
                css={{
                  color: color.textIcon.link,
                  fontSize: fontSize.h7,
                }}
              >
                Not Complete Yet
              </span>
            </>
          )}

          {data.completeState == ScheduleTimeSlotCompleteState.Missed && (
            <>
              <img src={WarningIcon} width={14} alt="Missed warning icon" />
              <span
                css={{
                  color: color.task.orange,
                  fontSize: fontSize.h7,
                }}
              >
                Missed
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  const GarminConnectCollectedStatusRow = (
    dataName: string,
    collectedStatus: boolean | undefined,
  ) => {
    return (
      <div
        css={{
          width: "100%",
          height: 32,
          maxHeight: 32,
          padding: "8px 16px",
          display: "flex",
          gap: 16,
        }}
      >
        <span
          css={{
            width: 208,
            color: color.textIcon.secondary,
            fontSize: fontSize.h7,
          }}
        >
          {dataName}
        </span>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            css={{
              color: collectedStatus ? color.textIcon.title : color.textIcon.secondary,
              fontStyle: collectedStatus ? "normal" : "italic",
              fontWeight: fontWeight.bold,
              fontSize: fontSize.h7,
            }}
          >
            {collectedStatus ? "Collected" : "No data"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      css={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      {/* tab switch */}
      <div
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 32,
          borderBottom: `1px solid ${color.border.default}`,
        }}
      >
        {RIF(
          garminEnabled,
          <button
            onClick={() => setTab(Tab.DataCollection)}
            css={{
              height: "32px",
              minHeight: "32px",
              backgroundColor: "transparent",
              color: tab === Tab.DataCollection ? color.textIcon.link : color.textIcon.secondary,
              fontSize: fontSize.h7,
              fontWeight: tab === Tab.DataCollection ? fontWeight.thick : fontWeight.medium,
              border: "none",
              borderBottom: tab === Tab.DataCollection ? `2px solid ${color.border.blue}` : "none",
              cursor: "pointer",
            }}
          >
            Device Data Collection
          </button>,
        )}

        <button
          onClick={() => setTab(Tab.TaskCompletion)}
          css={{
            height: "32px",
            minHeight: "32px",
            backgroundColor: "transparent",
            color: tab === Tab.TaskCompletion ? color.textIcon.link : color.textIcon.secondary,
            fontSize: fontSize.h7,
            fontWeight: tab === Tab.TaskCompletion ? fontWeight.thick : fontWeight.medium,
            border: "none",
            borderBottom: tab === Tab.TaskCompletion ? `2px solid ${color.border.blue}` : "none",
            cursor: "pointer",
          }}
        >
          Task Completion
        </button>
      </div>
      {/* content */}
      <div
        css={{
          width: "100%",
          display: "flex",
          overflow: "hidden",
        }}
      >
        {/* data collection */}
        <div
          css={{
            width: tab === Tab.DataCollection ? "100%" : 0,
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            gap: 40,
            transition: ".5s ease-in-out",
            overflow: "hidden",
          }}
        >
          {/* garmin connect app */}
          {garminConnectEnabled && (
            <div
              css={{
                width: "368px",
                minWidth: "368px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <img src={GarminConnectIcon} width={16} alt="Garmin Connect icon" />
                <span
                  css={{
                    fontWeight: fontWeight.bold,
                  }}
                >
                  Garmin Connect App
                </span>
                <Tooltip content="Data collected from Garmin Connect app" />
              </div>
              <div
                css={{
                  width: "100%",
                  borderRadius: 8,
                  border: `1px solid ${color.border.light}`,
                  overflow: "hidden",
                }}
              >
                <div
                  css={{
                    width: "100%",
                    padding: "8px 16px",
                    backgroundColor: color.surface.default,
                    display: "flex",
                    gap: 16,
                  }}
                >
                  <span
                    css={{
                      width: 208,
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Data Type
                  </span>
                  <span
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Status
                  </span>
                </div>
                {GarminConnectCollectedStatusRow(
                  "Daily Summary",
                  garminConnectCollectionStatus?.healthDailiesCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Activity Summary",
                  garminConnectCollectionStatus?.healthEpochsCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Sleep",
                  garminConnectCollectionStatus?.healthSleepsCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Steps",
                  garminConnectCollectionStatus?.healthDailiesCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Heart Rate",
                  garminConnectCollectionStatus?.healthDailiesCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Stress",
                  garminConnectCollectionStatus?.healthStressCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Respiration",
                  garminConnectCollectionStatus?.healthRespirationCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Oxygen Saturation",
                  garminConnectCollectionStatus?.healthPulseOxCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Skin Temperature",
                  garminConnectCollectionStatus?.healthSkinTemperatureCollected,
                )}
                {GarminConnectCollectedStatusRow(
                  "Body Composition(requires scale)",
                  garminConnectCollectionStatus?.healthBodyCompositionsCollected,
                )}
              </div>
            </div>
          )}
          {/* labfront companion app */}
          {garminDirectEnabled && (
            <div
              css={{
                width: "368px",
                minWidth: "368px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <img src={LabfrontIcon} width={16} alt="Labfront Companion icon" />
                <span
                  css={{
                    fontWeight: fontWeight.bold,
                  }}
                >
                  Labfront Companion App
                </span>
                <Tooltip content="Data collected from Labfront Companion app" />
              </div>
              <div
                css={{
                  width: "100%",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: `1px solid ${color.border.light}`,
                }}
              >
                <div
                  css={{
                    width: "100%",
                    padding: "8px 16px",
                    backgroundColor: color.surface.default,
                    display: "flex",
                    gap: 16,
                  }}
                >
                  <span
                    css={{
                      width: 208,
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Data Type
                  </span>
                  <span
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                    }}
                  >
                    Status
                  </span>
                </div>
                {Object.entries(garminDeviceTask?.garminDevice ?? {})
                  .filter(([key, value]) => key.endsWith("Enable") && value)
                  .map(([dataType]) => {
                    const adherenceKey = dataType.replace(/Enable$/i, "");
                    const dataCollected =
                      garminDirectAdherence?.[adherenceKey as keyof AdherenceGarminDirect];
                    return (
                      <div
                        key={dataType}
                        css={{
                          width: "100%",
                          height: 32,
                          maxHeight: 32,
                          padding: "8px 16px",
                          display: "flex",
                          gap: 16,
                        }}
                      >
                        <span
                          css={{
                            width: 208,
                            color: color.textIcon.secondary,
                            fontSize: fontSize.h7,
                          }}
                        >
                          {toReadable(dataType)}
                        </span>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            css={{
                              color: dataCollected
                                ? color.textIcon.title
                                : color.textIcon.secondary,
                              fontWeight: dataCollected ? fontWeight.bold : fontWeight.medium,
                              fontSize: fontSize.h7,
                              fontStyle: dataCollected ? "normal" : "italic",
                            }}
                          >
                            {dataCollected ? "Collected" : "No data"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
        {/* task completion */}
        <div
          css={{
            width: tab === Tab.TaskCompletion ? "100%" : 0,
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            gap: 40,
            transition: ".5s ease-in-out",
            overflow: "hidden",
          }}
        >
          {RIF(scheduleTaskRecordMap, <ScheduleTaskCompletion {...scheduleTaskRecordMap} />)}
          {RIF(
            filteredRegularTaskCompletion,
            <RegularTaskCompletion {...filteredRegularTaskCompletion} />,
          )}
        </div>
      </div>
    </div>
  );
};

const TaskDot = (props: { backgroundColor: string; text: string }) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const { backgroundColor, text } = props;
  return (
    <div
      css={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor,
        color: color.white,
        fontSize: fontSize.h7,
        fontWeight: fontWeight.thick,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
};

// const GarminConnectBlock = (props: {deviceType: string; garminConnectData?: AdherenceGarminConnect}) => {
//   const {color, fontSize, fontWeight} = useSelectTheme()
//   const {deviceType, garminConnectData} = props
//   // const [enableAmount, setEnableAmount] = useState(0)
//   const [collectedAmount, setCollectedAmount] = useState(0)

//   const displayingDataTypeList = [
//     'healthDailiesCollected',
//     'healthEpochsCollected',
//     'healthPulseOxCollected',
//     'healthRespirationCollected',
//     'healthSleepsCollected',
//     'healthStressCollected',
//     'healthBodyCompositionsCollected',
//     'womenHealthMCTCollected',
//   ]

//   useEffect(() => {
//     setCollectedAmount(0)
//     if (!garminConnectData) return
//     Object.entries(garminConnectData).forEach(([key, value]) => {
//       if (displayingDataTypeList.includes(key) && value) {
//         if (key === 'healthDailiesCollected') {
//           // healthDailiesCollected includes three types of data
//           return setCollectedAmount((prev) => prev + 3)
//         }
//         setCollectedAmount((prev) => prev + 1)
//       }
//     })
//   }, [garminConnectData])

//   return (
//     <div
//       css={{
//         border: `1px solid ${color.grey_160}`,
//         borderRadius: '5px',
//         width: '100%',
//         padding: '20px',
//         paddingBottom: '5px',
//       }}
//     >
//       <div css={{display: 'flex', paddingBottom: '15px'}}>
//         <img src={GarminConnectIcon} width='24' height='24' css={{marginRight: '12px'}} />
//         <div>
//           <div css={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
//             <div
//               css={{
//                 width: '13px',
//                 height: '13px',
//                 borderRadius: '50%',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//               }}
//             >
//               <img src={GarminGIcon} width='13' />
//             </div>
//             <div css={{fontWeight: fontWeight.thin, margin: '0 6px'}}>Garmin Connect App</div>
//             <Tooltip pseudoPosition='90%' content='Data collected from Garmin Connect app' />
//           </div>
//           <div
//             css={{
//               display: 'flex',
//               alignItems: 'center',
//             }}
//           >
//             <div css={{background: color.task.green, width: '7px', height: '7px', marginRight: '6px'}} />
//             <div css={{fontWeight: fontWeight.medium, fontSize: fontSize.h7}}>
//               {collectedAmount}/{displayingDataTypeList.length + 2} Metric(s) Collected
//             </div>
//           </div>
//         </div>
//       </div>

//       <GarminCollectItem
//         {...{
//           name: 'Daily Summary',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthDailiesCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Activity Summary',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthEpochsCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Sleep',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthSleepsCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Heart Rate',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthDailiesCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Stress',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthStressCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Steps',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthDailiesCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Respiration',
//           display: true,
//           deviceSupport: devicesSupportTable(deviceType, 'Respiration'),
//           collected: !!garminConnectData?.healthRespirationCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Oxygen Saturation',
//           display: true,
//           deviceSupport: devicesSupportTable(deviceType, 'Oxygen Saturation'),
//           collected: !!garminConnectData?.healthPulseOxCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Body Composition',
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.healthBodyCompositionsCollected,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: `Women's Health Tracking`,
//           display: true,
//           deviceSupport: true,
//           collected: !!garminConnectData?.womenHealthMCTCollected,
//         }}
//       />
//     </div>
//   )
// }

// interface GarminDirectBlockProps {
//   taskGarminData: AdherenceGarminTask | undefined
//   deviceType: string
//   taskGarminDeviceFromMethod: any
// }

// const GarminDirectBlock = (props: GarminDirectBlockProps) => {
//   const {color, fontSize, fontWeight} = useSelectTheme()
//   const {taskGarminData, deviceType, taskGarminDeviceFromMethod} = props
//   const [collectedAmount, setCollectedAmount] = useState(0)
//   const [enableAmount, setEnableAmount] = useState(0)
//   const [wearingDevice, setWearingDevice] = useState(true)

//   useEffect(() => {
//     if (!taskGarminDeviceFromMethod) return
//     setEnableAmount(
//       Object.entries(taskGarminDeviceFromMethod).filter(
//         ([key, value]) =>
//           !['acceleraometerEnable', 'enhancedBbiEnable', 'batteryPercentageSupport'].includes(key) &&
//           key.includes('Enable') &&
//           value,
//       ).length,
//     )
//   }, [taskGarminDeviceFromMethod])

//   useEffect(() => {
//     if (taskGarminData) {
//       setCollectedAmount(Object.values(taskGarminData).filter((item) => item === true).length)
//     } else {
//       setCollectedAmount(0)
//     }

//     // detect whether user wear the device or not, this affect display the garmin content or not
//     // only zero crossing & step will keep collect when not wearing
//     if (
//       !taskGarminData?.bbi &&
//       !taskGarminData?.hr &&
//       !taskGarminData?.pulseOx &&
//       !taskGarminData?.stress &&
//       !taskGarminData?.respiration &&
//       !taskGarminData?.actigraphy &&
//       !taskGarminData?.temperature
//     ) {
//       setWearingDevice(false)
//     } else {
//       setWearingDevice(true)
//     }
//   }, [taskGarminData])

//   // BE bug, collectedAmount should not higher than enableAmount
//   useEffect(() => {
//     if (collectedAmount > enableAmount) setCollectedAmount(enableAmount)
//   }, [enableAmount, collectedAmount])

//   return (
//     <div
//       css={{
//         border: `1px solid ${color.grey_160}`,
//         borderRadius: '5px',
//         width: '100%',
//         marginTop: '10px',
//         padding: '20px',
//         paddingBottom: '5px',
//       }}
//     >
//       <div css={{display: 'flex', paddingBottom: '15px'}}>
//         <img src={LabfrontIcon} width='24' height='24' css={{marginRight: '12px'}} />
//         <div>
//           <div css={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
//             <div
//               css={{
//                 width: '13px',
//                 height: '13px',
//                 borderRadius: '50%',
//                 paddingBottom: '1px',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//               }}
//             >
//               <img src={GarminLIcon} width='13' />
//             </div>
//             <div css={{fontWeight: fontWeight.thin, margin: '0 6px'}}>Labfront Companion App</div>
//             <Tooltip pseudoPosition='90%' content='Data collected from Labfront Companion app' />
//           </div>
//           <div
//             css={{
//               display: 'flex',
//               alignItems: 'center',
//             }}
//           >
//             <div css={{background: color.task.green, width: '7px', height: '7px', marginRight: '6px'}} />
//             <div css={{fontWeight: fontWeight.medium, fontSize: fontSize.h7}}>
//               {wearingDevice ? collectedAmount : '0'}/{enableAmount} Metric(s) Collected
//             </div>
//           </div>
//         </div>
//       </div>
//       <GarminCollectItem
//         {...{
//           name: 'Heart Rate',
//           collected: wearingDevice && !!taskGarminData?.hr,
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.heartRateEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Stress',
//           collected: wearingDevice && !!taskGarminData?.stress,
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.stressEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Steps',
//           collected: wearingDevice && !!taskGarminData?.steps,
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.stepsEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Respiration',
//           collected: wearingDevice && !!taskGarminData?.respiration,
//           deviceSupport: devicesSupportTable(deviceType, 'Respiration'),
//           display: taskGarminDeviceFromMethod?.respirationEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Oxygen Saturation',
//           collected: wearingDevice && !!taskGarminData?.pulseOx,
//           deviceSupport: devicesSupportTable(deviceType, 'Oxygen Saturation'),
//           display: taskGarminDeviceFromMethod?.pulseOxEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Actigraphy',
//           collected: wearingDevice && !!taskGarminData?.actigraphy,
//           // deviceSupport: devicesSupportTable(deviceType, 'Actigraphy'),
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.actigraphyEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Heart Rate Interval',
//           collected: wearingDevice && !!taskGarminData?.bbi,
//           deviceSupport: true,
//           taskGarminData,
//           display: taskGarminDeviceFromMethod?.bbiEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Zero Crossings',
//           collected: wearingDevice && !!taskGarminData?.zeroCrossing,
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.zeroCrossingEnable,
//         }}
//       />
//       <GarminCollectItem
//         {...{
//           name: 'Skin Temperature',
//           collected: wearingDevice && !!taskGarminData?.temperature,
//           deviceSupport: true,
//           display: taskGarminDeviceFromMethod?.temperatureEnable,
//         }}
//       />
//     </div>
//   )
// }

// interface GarminCollectItemProps {
//   name: string
//   collected: boolean
//   deviceSupport: boolean
//   taskGarminData?: AdherenceGarminTask
//   display: boolean
// }

// const GarminCollectItem = (props: GarminCollectItemProps) => {
//   const {color, fontSize, fontWeight} = useSelectTheme()
//   const {name, collected, taskGarminData, deviceSupport, display} = props
//   const unavailable = !deviceSupport
//   return (
//     <div
//       css={{
//         display: display ? 'flex' : 'none',
//         flexDirection: 'column',
//       }}
//     >
//       <div
//         css={{
//           borderTop: `1px solid ${color.grey_100}`,
//           padding: '12px 0 8px 0',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'space-between',
//         }}
//       >
//         <div css={{display: 'flex', alignItems: 'center'}}>
//           <div
//             css={{
//               background: collected ? color.task.green : color.surface.disabled,
//               width: '7px',
//               height: '7px',
//               marginRight: '6px',
//             }}
//           />
//           <div
//             css={{
//               color: collected ? '' : color.grey_400,
//               fontWeight: fontWeight.medium,
//               fontSize: fontSize.h7,
//             }}
//           >
//             {name}
//           </div>
//         </div>
//         <div
//           css={{
//             display: 'flex',
//             fontWeight: fontWeight.medium,
//             fontSize: fontSize.h7,
//             color: collected ? color.task.green : color.grey_400,
//           }}
//         >
//           {unavailable ? 'Not available' : collected ? 'Collected' : '--'}
//           {RIF(
//             unavailable,
//             <Tooltip
//               css={{marginLeft: '6px'}}
//               pseudoPosition='90%'
//               content="This participant's Garmin device doesn't collect this type of data."
//             />,
//           )}
//         </div>
//       </div>
//       {RIF(
//         name === 'Heart Rate Interval' && collected,
//         <div
//           css={{
//             paddingBottom: '12px',
//             paddingLeft: '13px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'space-between',
//           }}
//         >
//           <div css={{fontSize: fontSize.h7, display: 'flex'}}>
//             Total Accumulated BBI
//             <Tooltip
//               pseudoPosition='90%'
//               css={{marginLeft: '6px'}}
//               content='Total accumulated BBI refers to the total amount of time BBI has been collected so far today. A lower than average total could signal the participant is not wearing their smartwatch as often.'
//             />
//           </div>
//           <div css={{color: color.primary, fontWeight: fontWeight.thick, fontSize: fontSize.h7}}>
//             {taskGarminData?.bbiMSAccumulated &&
//               (timeConvert({time: taskGarminData?.bbiMSAccumulated, type: TimeConvertType.bbiAccumulated}) as string)}
//           </div>
//         </div>,
//       )}
//     </div>
//   )
// }

// interface TaskBlockProps {
//   name: string
//   done: boolean
//   IconIndex: number
//   IconColor: string
//   adherenceTask?: AdherenceNormalTask
// }

// const TaskBlock = (props: TaskBlockProps) => {
//   const {color, fontSize, fontWeight, colorTranslator} = useSelectTheme()
//   const {name, done, IconIndex, IconColor, adherenceTask} = props
//   const method = useSelectMethod()
//   const taskType = adherenceTask?.taskType
//   const repetition = adherenceTask?.timestampList?.length
//   const taskColor = IconColor ? IconColor : colorTranslator(IconIndex - 1)
//   const movesenseTask = _.find(method.taskList, (item) => item.id === adherenceTask?.taskId)?.stopwatchMovesenseStream
//   const movesenseWording = movesenseTask && convertMovesenseDescription(movesenseTask)
//   const garminStreamTask = _.find(method.taskList, (item) => item.id === adherenceTask?.taskId)?.stopwatchGarminStream
//   const garminStreamWording = garminStreamTask && convertGarminStreamDescription(garminStreamTask)

//   return (
//     <>
//       <div
//         css={{
//           border: `1px ${done ? 'solid' : 'dashed'} ${color.grey_160}`,
//           borderRadius: '5px',
//           width: '100%',
//           marginTop: '10px',
//           padding: '15px 20px',
//         }}
//       >
//         <div
//           css={{
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'space-between',
//           }}
//         >
//           <div
//             style={
//               {
//                 border: !done && `1px dashed ${color.grey_160}`,
//                 background: done && taskColor,
//                 borderRadius: '50%',
//                 width: '18px',
//                 height: '18px',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 fontSize: fontSize.h7,
//                 fontWeight: fontWeight.thin,
//                 color: done ? color.white : color.grey_160,
//               } as CSSProperties
//             }
//           >
//             {IconIndex + 1}
//           </div>
//           <div css={{fontSize: fontSize.h7, color: done ? '' : color.grey_400}}>
//             {done ? `${repetition} Time(s)` : 'Not Completed'}
//           </div>
//         </div>
//         <div css={{fontWeight: fontWeight.thin, marginTop: '8px', color: done ? '' : color.grey_400}}>{name}</div>
//         {RIF(
//           taskType === TaskType.StopwatchMovesenseStream || taskType === TaskType.StopwatchMovesenseLogData,
//           <div css={{color: color.grey_600, fontSize: '12px', whiteSpace: 'pre-line', marginTop: '8px'}}>
//             {movesenseWording}
//           </div>,
//         )}
//         {RIF(
//           taskType === 'stopwatch_garmin_stream',
//           <div css={{color: color.grey_600, fontSize: '12px', whiteSpace: 'pre-line', marginTop: '8px'}}>
//             {garminStreamWording}
//           </div>,
//         )}
//         {RIF(
//           done && (taskType === 'todo' || taskType === 'questionnaire'),
//           <div css={{marginTop: '8px'}}>
//             {adherenceTask?.timestampList?.map((item: TaskRecordTimestamp, index: number) => (
//               <li
//                 key={index}
//                 css={{
//                   paddingTop: '8px',
//                   marginTop: '8px',
//                   fontSize: fontSize.h7,
//                   borderTop: `1px solid ${color.grey_100}`,
//                 }}
//               >
//                 Task Completed at&nbsp;
//                 <span css={{fontSize: fontSize.h7, color: color.primary}}>
//                   {
//                     timeConvert({
//                       time: item?.unixTimestampComplete + item.timeOffset,
//                       type: TimeConvertType.localizedUtcCustomFormat,
//                       arg: 'h:mm aa',
//                     }) as string
//                   }
//                   {/* ({item.timezone}) */}
//                 </span>
//               </li>
//             ))}
//           </div>,
//         )}
//         {RIF(
//           (done && taskType === 'timer') ||
//             taskType === TaskType.StopwatchMovesenseStream ||
//             taskType === TaskType.StopwatchMovesenseLogData ||
//             taskType === TaskType.StopwatchGarminStream,
//           <div css={{marginTop: '8px'}}>
//             {adherenceTask?.timestampList?.map((item: TaskRecordTimestamp, index: number) => (
//               <li
//                 key={index}
//                 css={{
//                   paddingTop: '8px',
//                   marginTop: '8px',
//                   fontSize: fontSize.h7,
//                   borderTop: `1px solid ${color.grey_100}`,
//                 }}
//               >
//                 Task Duration:&nbsp;
//                 <span css={{fontSize: '12px', color: color.primary}}>
//                   {
//                     timeConvert({
//                       time: item?.unixTimestampStart,
//                       type: TimeConvertType.timerDuration,
//                       arg: item?.unixTimestampComplete,
//                     }) as string
//                   }
//                 </span>
//                 {` (${timeConvert({
//                   time: (item?.unixTimestampStart ?? 0) + item.timeOffset,
//                   type: TimeConvertType.localizedUtcCustomFormat,
//                   arg: 'h:mm aa',
//                 })} - ${timeConvert({
//                   time: item?.unixTimestampComplete + item.timeOffset,
//                   type: TimeConvertType.localizedUtcCustomFormat,
//                   arg: 'h:mm aa',
//                 })})`}
//                 {/* ({item.timezone}) */}
//               </li>
//             ))}
//           </div>,
//         )}
//       </div>
//     </>
//   )
// }

// // TODO: update when new launch ,& implement acc & battery percentage
// const devicesSupportTable = (deviceType: string, collectData: string) => {
//   switch (deviceType) {
//     case 'Vivosmart4':
//       if (collectData === 'Respiration') return false
//       break
//     case 'Instinct':
//       if (collectData === 'Oxygen Saturation') return false
//       if (collectData === 'Respiration') return false
//       break
//     case 'InstinctSolar':
//       if (collectData === 'Respiration') return false
//       break
//     case 'Forerunner45':
//       if (collectData === 'Oxygen Saturation') return false
//       if (collectData === 'Respiration') return false
//       break
//     case 'Forerunner55':
//       if (collectData === 'Oxygen Saturation') return false
//       break
//   }
//   return true
// }

// interface DexcomBlockProps {
//   egvCounts: number
//   deviceType?: string
// }

// const DexcomBlock = (props: DexcomBlockProps) => {
//   const {color, fontSize, fontWeight} = useSelectTheme()
//   const {egvCounts} = props
//   const [collectedAmount, setCollectedAmount] = useState(0)
//   const [dataCollected, setDataCollected] = useState(false)

//   useEffect(() => {
//     setCollectedAmount(egvCounts > 0 ? 1 : 0)
//     setDataCollected(egvCounts > 0)
//   }, [egvCounts])

//   return (
//     <div
//       css={{
//         border: `1px solid ${color.grey_160}`,
//         borderRadius: '5px',
//         width: '100%',
//         marginTop: '10px',
//         padding: '20px',
//         paddingBottom: '5px',
//       }}
//     >
//       <div css={{display: 'flex', paddingBottom: '15px'}}>
//         <img src={DexcomIcon} width='24' height='24' css={{marginRight: '12px'}} />
//         <div>
//           <div css={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
//             <div
//               css={{
//                 width: '13px',
//                 height: '13px',
//                 borderRadius: '50%',
//                 paddingBottom: '1px',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//               }}
//             >
//               {/* <img src={GarminLIcon} width='13' />*/}
//               <div
//                 style={
//                   {
//                     border: `1px ${color.dexcomGreen}`,
//                     background: color.dexcomGreen,
//                     borderRadius: '50%',
//                     width: '13px',
//                     height: '13x',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     fontSize: fontSize.h7,
//                     fontWeight: fontWeight.thin,
//                     color: color.white,
//                   } as CSSProperties
//                 }
//               >
//                 D
//               </div>
//             </div>
//             <div css={{fontWeight: fontWeight.thin, margin: '0 6px'}}>Dexcom</div>
//             <Tooltip pseudoPosition='90%' content='Data collected from Dexcom app' />
//           </div>
//           <div
//             css={{
//               display: 'flex',
//               alignItems: 'center',
//             }}
//           >
//             <div css={{background: color.task.green, width: '7px', height: '7px', marginRight: '6px'}} />
//             <div css={{fontWeight: fontWeight.medium, fontSize: fontSize.h7}}>
//               {collectedAmount}/{1} Metric(s) Collected
//             </div>
//           </div>
//         </div>
//       </div>
//       <DexcomCollectItem
//         {...{
//           name: 'Continuous Glucose Reading',
//           collected: collectedAmount > 0,
//           egvDataCount: egvCounts,
//           display: true,
//         }}
//       />
//     </div>
//   )
// }

// interface DexcomCollectItemProps {
//   name: string
//   collected: boolean
//   egvDataCount: number
//   display: boolean
// }

// const DexcomCollectItem = (props: DexcomCollectItemProps) => {
//   const {color, fontSize, fontWeight} = useSelectTheme()
//   const {name, collected, egvDataCount, display} = props
//   return (
//     <div
//       css={{
//         display: display ? 'flex' : 'none',
//         flexDirection: 'column',
//       }}
//     >
//       <div
//         css={{
//           borderTop: `1px solid ${color.grey_100}`,
//           padding: '12px 0 8px 0',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'space-between',
//         }}
//       >
//         <div css={{display: 'flex', alignItems: 'center'}}>
//           <div
//             css={{
//               background: collected ? color.task.green : color.surface.disabled,
//               width: '7px',
//               height: '7px',
//               marginRight: '6px',
//             }}
//           />
//           <div
//             css={{
//               color: collected ? '' : color.grey_400,
//               fontWeight: fontWeight.medium,
//               fontSize: fontSize.h7,
//             }}
//           >
//             {name}

//             {RIF(
//               collected,
//               <div>
//                 <div css={{fontSize: fontSize.h7, display: 'inline'}}>Estimate collected duration: </div>
//                 <div
//                   css={{
//                     color: color.primary,
//                     fontWeight: fontWeight.thick,
//                     fontSize: fontSize.h7,
//                     display: 'inline-block',
//                   }}
//                 >
//                   {
//                     timeConvert({
//                       time: egvDataCount * 300000,
//                       type: TimeConvertType.bbiAccumulated,
//                     }) as string
//                   }
//                 </div>
//               </div>,
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   )
// }
